import { cloneDeep, every } from 'lodash';

var capSettingType = {
  COUNT: 'count',
  SUM: 'sum',
  BOTH: 'both'
};

var commonCapFieldTranslations = {
  allTimeSumCap: {
    label: 'GLOBAL_SUM_CAP_LABEL',
    tooltip: 'GLOBAL_CAP_TT'
  },
  monthlySumCap: {
    label: 'MONTHLY_SUM_CAP_LABEL',
    tooltip: 'MONTHLY_CAP_TT'
  },
  dailySumCap: {
    label: 'DAILY_SUM_CAP_LABEL',
    tooltip: 'DAILY_CAP_TT'
  },
  hourlySumCap: {
    label: 'HOURLY_SUM_CAP_LABEL',
    tooltip: 'HOURLY_CAP_TT'
  }
};

var buyerCapFieldsTranslation = {
  capCategory: 'buyer',
  capCriteria: 'Revenue'
};

var capSettings = [
  {
    key: 'allTimeSumCap',
    capSettingType: capSettingType.SUM
  },
  {
    key: 'allTimeCap',
    capSettingType: capSettingType.COUNT
  },
  {
    key: 'monthlySumCap',
    capSettingType: capSettingType.SUM
  },
  {
    key: 'monthlyCap',
    capSettingType: capSettingType.COUNT
  },
  {
    key: 'dailySumCap',
    capSettingType: capSettingType.SUM
  },
  {
    key: 'dailyCap',
    capSettingType: capSettingType.COUNT
  },
  {
    key: 'hourlySumCap',
    capSettingType: capSettingType.SUM
  },
  {
    key: 'hourlyCap',
    capSettingType: capSettingType.COUNT
  }
];

export default class capSettingsService {
  /** @ngInject **/
  constructor(_) {
    this._ = _;
    this.capSettingType = cloneDeep(capSettingType);
    this.commonCapFieldTranslations = cloneDeep(commonCapFieldTranslations);
    this.buyerCapFieldsTranslation = cloneDeep(buyerCapFieldsTranslation);
    this.defaultCapSettingsType = capSettingType.COUNT;
  }

  /*
    This function is responsible for transitioning the value off from the simple field and into the array per day of week
  */
  sanitizeCapPerDayOfWeekFromSingleValue(schedule, singleName, arrayName) {
    // If value per day of week already exists, abort
    if (schedule[arrayName] && schedule[arrayName].length > 0) {
      return;
    }

    // If single value is unset, abort
    if (schedule[singleName] == -1) {
      return;
    }

    // Populates the cap per day of week off the single value
    schedule[arrayName] = [];
    for (let dow = 0; dow < 7; dow++) {
      schedule[arrayName][dow] = schedule[singleName];
    }

    // Value has been moved off to the per day of week, moves the single value to unset
    schedule[singleName] = -1;
  }

  sanitizeDOWCapToStore(dailyCapDayOfWeek) {
    if (this.#isCapPerDayOfWeekValid(dailyCapDayOfWeek)) {
      for (let dow = 0; dow < 7; dow++) {
        if (dailyCapDayOfWeek[dow] == null) {
          dailyCapDayOfWeek[dow] = -1;
        }
      }

      return dailyCapDayOfWeek;
    }

    return null;
  }

  sanitizeDOWCapToDisplay(dailyCapDayOfWeek) {
    if (dailyCapDayOfWeek) {
      for (let dow = 0; dow < 7; dow++) {
        if (dailyCapDayOfWeek[dow] == -1) {
          dailyCapDayOfWeek[dow] = null;
        }
      }

      return dailyCapDayOfWeek;
    }

    return [];
  }

  createEmptyDayOfWeekCaps() {
    let matrix = new Array();
    for (let day = 0; day < 7; day++) {
      matrix.push(undefined);
    }
    return matrix;
  }

  cloneDayOfWeekCaps(source) {
    let matrix = new Array();
    if (source && Array.isArray(source)) {
      for (let day = 0; day < 7; day++) {
        matrix.push(source[day]);
      }
    }
    return matrix;
  }

  getCapSettings(
    callCaps,
    settingsObjectContainer,
    capSettingType,
    additionalSettings = []
  ) {
    const settings = [];
    this.#concatAdditionalSettingsToFilteredCapSettings(
      capSettingType,
      additionalSettings
    ).forEach(function(cap) {
      settings.push({
        key: cap.key,
        model: callCaps[cap.key] ?? -1,
        modelSettings: settingsObjectContainer[cap.key],
        capSettingType: cap.capSettingType
      });
    });
    return settings;
  }

  removeCaps(capSettingsList, capSettingsContainer, callCaps) {
    capSettingsList?.forEach(function(cap) {
      delete capSettingsContainer[cap.key];
      callCaps[cap.key] = -1;
    });
  }

  initializeCallCaps(
    capSettingType,
    callCaps,
    settingsObjectContainer,
    additionalSettings = []
  ) {
    if (callCaps && settingsObjectContainer) {
      this.#concatAdditionalSettingsToFilteredCapSettings(
        capSettingType,
        additionalSettings
      ).forEach(function(cap) {
        if (settingsObjectContainer[cap.key] >= 0) {
          callCaps[cap.key] = 1;
        }
      });
    }
    return callCaps;
  }

  initializeCallCapSettings(
    capSettingType,
    settingsObjectContainer,
    additionalSettings = []
  ) {
    if (settingsObjectContainer) {
      this.#concatAdditionalSettingsToFilteredCapSettings(
        capSettingType,
        additionalSettings
      ).forEach(function(cap) {
        if (settingsObjectContainer[cap.key] >= 0 == false) {
          delete settingsObjectContainer[cap.key];
        }
      });
    }
    return settingsObjectContainer;
  }

  resetCallCaps(capSettingType, callCaps, settingsObjectContainer) {
    if (callCaps && settingsObjectContainer) {
      this.#getFilteredCapSettings(capSettingType).forEach(function(cap) {
        if (callCaps[cap.key] === -1) {
          settingsObjectContainer[cap.key] = -1;
        }
      });
    }
  }

  #getFilteredCapSettings(capSettingTypeInput) {
    return this._.filter(capSettings, function(cap) {
      return (
        capSettingTypeInput === capSettingType.BOTH ||
        cap.capSettingType === capSettingTypeInput
      );
    });
  }

  #concatAdditionalSettingsToFilteredCapSettings(
    capSettingTypeInput,
    additionalSettings
  ) {
    return this.#getFilteredCapSettings(capSettingTypeInput).concat(
      additionalSettings
    );
  }

  #isCapPerDayOfWeekValid(dowArray) {
    if (!dowArray) {
      return false;
    }

    return !every(dowArray, function(day) {
      return day == null;
    });
  }
}
